import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import * as Sentry from "@sentry/react";
import { SentryErrorBoundary } from "@/components/SentryErrorBoundary";
import { SentryTestButton } from "@/components/SentryTestButton";
import { ThemeProvider } from "next-themes";
import { AuthProvider } from "./hooks/useAuth";
import { LanguageProvider } from "./contexts/LanguageContext";
import { ProductProvider } from "./contexts/ProductContext";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { AppLayout } from "./components/AppLayout";
import Index from "./pages/Index";
import CustomersSaas from "./pages/CustomersSaas";
import Transactions from "./pages/Transactions";
import Metrics from "./pages/Metrics";
import Integrations from "./pages/Integrations";
import Import from "./pages/Import";
import Reports from "./pages/Reports";
import Settings from "./pages/Settings";
import Account from "./pages/Account";
import Plans from "./pages/Plans";
import PaymentGateway from "./pages/PaymentGateway";
import Notifications from "./pages/Notifications";
import Auth from "./pages/Auth";
import ResetPassword from "./pages/ResetPassword";
import NotFound from "./pages/NotFound";
import CheckoutSuccess from "./pages/CheckoutSuccess";
import CheckoutCancel from "./pages/CheckoutCancel";
import Landing from "./pages/Landing";
import PricingDetails from "./pages/PricingDetails";
import TermsOfService from "./pages/TermsOfService";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import Products from "./pages/Products";
import { SuperAdminRoute } from "./components/SuperAdminRoute";
import { ThemeSync } from "@/contexts/ThemeContext";
import AdminSeed from "./pages/AdminSeed";
import AdminTenantManagement from "./pages/AdminTenantManagement";
import HeadScripts from "./pages/HeadScripts";

const queryClient = new QueryClient();

const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);

const App = () => (
  <SentryErrorBoundary>
    <QueryClientProvider client={queryClient}>
    <ThemeProvider attribute="class" defaultTheme="light" enableSystem={false}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <AuthProvider>
            <LanguageProvider>
              <ProductProvider>
                <ThemeSync />
                <SentryTestButton />
                <SentryRoutes>
                  <Route path="/" element={<Landing />} />
                  <Route path="/auth" element={<Auth />} />
                  <Route path="/reset-password" element={<ResetPassword />} />
                  <Route path="/admin/seed-demo" element={<AdminSeed />} />
                  <Route path="/pricing-details" element={<PricingDetails />} />
                  <Route path="/terms-of-service" element={<TermsOfService />} />
                  <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                  <Route path="/checkout-success" element={<ProtectedRoute><CheckoutSuccess /></ProtectedRoute>} />
                  <Route path="/checkout-cancel" element={<ProtectedRoute><CheckoutCancel /></ProtectedRoute>} />
                  <Route element={<ProtectedRoute><AppLayout /></ProtectedRoute>}>
                    <Route path="/dashboard" element={<Index />} />
                    <Route path="/customers-saas" element={<CustomersSaas />} />
                    <Route path="/transactions" element={<Transactions />} />
                    <Route path="/metrics" element={<Metrics />} />
                    <Route path="/integrations" element={<Integrations />} />
                    <Route path="/import" element={<Import />} />
                    <Route path="/reports" element={<Reports />} />
                    <Route path="/products" element={<Products />} />
                    <Route path="/settings" element={<Settings />} />
                    <Route path="/account" element={<Account />} />
                    <Route path="/notifications" element={<Notifications />} />
                    <Route path="/plans" element={<SuperAdminRoute><Plans /></SuperAdminRoute>} />
                    <Route path="/payment-gateway" element={<SuperAdminRoute><PaymentGateway /></SuperAdminRoute>} />
                    <Route path="/admin/tenants" element={<SuperAdminRoute><AdminTenantManagement /></SuperAdminRoute>} />
                    <Route path="/admin/head-scripts" element={<SuperAdminRoute><HeadScripts /></SuperAdminRoute>} />
                  </Route>
                  {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                  <Route path="*" element={<NotFound />} />
                </SentryRoutes>
              </ProductProvider>
            </LanguageProvider>
          </AuthProvider>
        </BrowserRouter>
      </TooltipProvider>
    </ThemeProvider>
  </QueryClientProvider>
  </SentryErrorBoundary>
);

export default App;
